import React, { useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import CommentIcon from '@mui/icons-material/Comment';

const TranscriptViewer = ({
  transcript = [],
  aiAnnotations = [],
  lecturerAnnotations = [],
  highlightedTurn,
  onTurnHover,
}) => {
  const turnRefs = useRef({});

  // Scroll to highlighted turn
  useEffect(() => {
    if (highlightedTurn != null && turnRefs.current[highlightedTurn]) {
      turnRefs.current[highlightedTurn].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightedTurn]);

  // Build annotation lookup by turn index
  const annotationsByTurn = {};
  aiAnnotations.forEach(a => {
    const idx = a.turnIndex;
    if (!annotationsByTurn[idx]) annotationsByTurn[idx] = { ai: [], lecturer: [] };
    annotationsByTurn[idx].ai.push(a);
  });
  lecturerAnnotations.forEach(a => {
    const idx = a.turnIndex;
    if (!annotationsByTurn[idx]) annotationsByTurn[idx] = { ai: [], lecturer: [] };
    annotationsByTurn[idx].lecturer.push(a);
  });

  if (transcript.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          No transcript available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Session Transcript</Typography>
      {transcript.map((msg) => {
        const isHighlighted = highlightedTurn === msg.turnIndex;
        const isStudent = msg.actor === 'student';
        const isSystem = msg.actor === 'system';
        const hasAnnotations = annotationsByTurn[msg.turnIndex];

        return (
          <Box
            key={msg.turnIndex}
            ref={el => { turnRefs.current[msg.turnIndex] = el; }}
            sx={{
              p: 1.5,
              mb: 1,
              borderRadius: 1,
              bgcolor: isHighlighted ? 'primary.light' : 'transparent',
              transition: 'background-color 0.3s ease',
              borderLeft: hasAnnotations ? '3px solid' : 'none',
              borderColor: hasAnnotations ? 'primary.main' : 'transparent',
              '&:hover': { bgcolor: isHighlighted ? 'primary.light' : 'grey.50' },
            }}
            onMouseEnter={() => onTurnHover && onTurnHover(msg.turnIndex)}
            onMouseLeave={() => onTurnHover && onTurnHover(null)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Chip
                label={`#${msg.turnIndex}`}
                size="small"
                sx={{ fontSize: '0.65rem', height: 18 }}
              />
              <Typography
                variant="caption"
                fontWeight={600}
                color={isStudent ? 'primary.main' : isSystem ? 'info.main' : 'text.secondary'}
              >
                {isStudent ? 'Student' : isSystem ? 'System' : 'Patient'}
              </Typography>
              {hasAnnotations && (
                <CommentIcon fontSize="small" color="primary" sx={{ ml: 'auto' }} />
              )}
            </Box>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', pl: 1 }}>
              {msg.content}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default TranscriptViewer;
